import { UnsubscribeSettings } from 'sections/client-management/entities';
import { maximumDelay } from '../configs/sendConfig';
import {
  ICycle,
  IPercentageSplit,
  IOffsiteCampaignType,
  ISegment,
  isVariantDefault,
  isVariantControlGroup,
  segmentHasMultipleControlGroups,
} from '../../entities/offsiteCampaign';
import { TFunction } from 'react-i18next';

export interface Progress {
  requiredFields: number;
  validFields: number;
  percent: number;
  messages: string[];
}

export const isSplitTestStartDateSelected = (startDate: moment.Moment, t: TFunction<'campaigns'>) => ({
  expression: startDate && startDate.valueOf() !== undefined,
  message: t('progress.validation.splitTestDate.startDate'),
});

export const isSplitTestEndDateSelected = (endDate: moment.Moment, t: TFunction<'campaigns'>) => ({
  expression: endDate && endDate.valueOf() !== undefined,
  message: t('progress.validation.splitTestDate.endDate'),
});

export const segmentNameValidation = (name: string, t: TFunction<'campaigns'>) => ({
  expression: name && name.length >= 1 && name.length <= 100,
  message: t('progress.validation.segmentRule.requiredCharacters'),
});

export const splitTestNameValidation = (name: string, t: TFunction<'campaigns'>) => ({
  expression: name && name.length >= 1 && name.length <= 100,
  message: t('progress.validation.splitName.requiredCharacters'),
});
export const splitNameValidation = (splitPercentages: IPercentageSplit[], t: TFunction<'campaigns'>) => ({
  expression:
    splitPercentages.length === 1 || splitPercentages.every(s => s.name && s.name.length >= 5 && s.name.length <= 100),
  message: t('progress.validation.splitRule.requiredCharacters'),
});

const splitPercentageValidation = (splitPercentages: IPercentageSplit[], t: TFunction<'campaigns'>) => ({
  expression: splitPercentages.reduce((sum, current) => sum + current.percentage, 0) === 100,
  message: t('progress.validation.splitPercentageRule.mustEqual100'),
});

export const getProgress = (
  segment: ISegment,
  sessionTimeout: number,
  unsubscribeSettings: UnsubscribeSettings,
  type: IOffsiteCampaignType,
  t: TFunction<'campaigns'>,
  featureSplitTest = false,
  featureControlGroup = false,
): Progress => {
  const unsubscribeSet =
    type !== 'cartAbandonment' ||
    unsubscribeSettings?.unsubscribeOption === 'UnsubscribeURL' ||
    unsubscribeSettings?.unsubscribeOption === 'GlobalUnsubscribe' ||
    unsubscribeSettings?.unsubscribeOption === 'UnsubscribeTemplate';

  const progressContext = { sessionTimeout };
  let allCycles = [] as ICycle[];
  let defaultIsControlGroup = false;
  const multipleControlGroups = segmentHasMultipleControlGroups(segment);
  segment.percentageSplits.forEach(s => {
    if (!defaultIsControlGroup) {
      defaultIsControlGroup = isVariantControlGroup(s) && isVariantDefault(s, segment.percentageSplits);
    }

    allCycles = allCycles.concat(s.cycles);
  });

  return add([
    validationProgress(segmentNameValidation(segment.name, t)),
    validationProgress(splitNameValidation(segment.percentageSplits, t)),
    validationProgress(splitPercentageValidation(segment.percentageSplits, t)),
    validationProgress({
      expression: unsubscribeSet === undefined || unsubscribeSet === true,
      message: t('progress.validation.cartAbandonment.unsubscribeRequired'),
    }),
    validationProgress({
      expression: !(featureControlGroup && featureSplitTest && defaultIsControlGroup),
      message: t('progress.validation.controlGroup.controlIsDefault'),
    }),
    validationProgress({
      expression: !(featureControlGroup && featureSplitTest && multipleControlGroups),
      message: t('progress.validation.controlGroup.multiple'),
    }),
    reduceProgresses(allCycles, progressContext, cycleProgress, t),
  ]);
};

function cycleProgress(cycle: ICycle, pc: ProgressContext, t: TFunction<'campaigns'>): Progress {
  switch (cycle.messageType) {
    case 'email': {
      return validationProgress(
        {
          expression: cycle.templateId,
          message: t('progress.validation.noTemplate', { channelType: cycle.messageType }),
        },
        {
          expression: cycle.delay >= pc.sessionTimeout,
          message: t('progress.validation.validSessionTimeout', { channelType: cycle.messageType }),
        },
        {
          expression: cycle.delay <= maximumDelay,
          message: t('progress.validation.validSendTime', { channelType: cycle.messageType }),
        },
        {
          expression: cycle.subjectLineId,
          message: t('progress.validation.validSubject', { channelType: cycle.messageType }),
        },
      );
    }
    case 'sms': {
      return validationProgress(
        {
          expression: cycle.templateId,
          message: t('progress.validation.noTemplate', { channelType: cycle.messageType }),
        },
        {
          expression: cycle.delay >= pc.sessionTimeout,
          message: t('progress.validation.validSessionTimeout', { channelType: cycle.messageType }),
        },
        {
          expression: cycle.delay <= maximumDelay,
          message: t('progress.validation.validSendTime', { channelType: cycle.messageType }),
        },
      );
    }
    case 'whatsapp': {
      return validationProgress(
        {
          expression: cycle.delay >= pc.sessionTimeout,
          message: t('progress.validation.validSessionTimeout', { channelType: cycle.messageType }),
        },
        {
          expression: cycle.delay <= maximumDelay,
          message: t('progress.validation.validSendTime', { channelType: cycle.messageType }),
        },
      );
    }
    case 'rcs': {
      return validationProgress(
        {
          expression: cycle.templateId,
          message: t('progress.validation.noTemplate', { channelType: cycle.messageType }),
        },
        {
          expression: cycle.delay >= pc.sessionTimeout,
          message: t('progress.validation.validSessionTimeout', { channelType: cycle.messageType }),
        },
        {
          expression: cycle.delay <= maximumDelay,
          message: t('progress.validation.validSendTime', { channelType: cycle.messageType }),
        },
      );
    }
  }
}

function toProgress(validFields: number, requiredFields: number, messages: string[] = []): Progress {
  return {
    percent: requiredFields === 0 ? 100 : (100 * validFields) / requiredFields,
    validFields,
    requiredFields,
    messages,
  };
}

type ProgressContext = {
  sessionTimeout: number;
};

function reduceProgresses<T>(
  ts: T[],
  pc: ProgressContext,
  f: (t: T, pc: ProgressContext, translator: TFunction<'campaigns'>) => Progress,
  translator: TFunction<'campaigns'>,
): Progress {
  return ts.reduce((acc, next) => add([acc, f(next, pc, translator)]), toProgress(0, 0));
}

function validationProgress(...conditions: { expression: any; message: string }[]): Progress {
  const requiredFields = conditions.length;
  const validFields = conditions.filter(x => x.expression).length;
  const invalidMessages = conditions.filter(x => !x.expression).map(x => x.message);
  return toProgress(validFields, requiredFields, invalidMessages);
}

function add(progress: Progress[]): Progress {
  return toProgress(
    progress.reduce((acc, curr) => acc + curr.validFields, 0),
    progress.reduce((acc, curr) => acc + curr.requiredFields, 0),
    progress.reduce((acc, curr): string[] => acc.concat(curr.messages), []),
  );
}
